/**
 * airan-ntn — Configuration Constants
 */

import { SimConfig, DEFAULT_CONFIG } from './types.js';

// ──────────────────────────────────────────────────────────────────────────────
// Physical Constants
// ──────────────────────────────────────────────────────────────────────────────

export const EARTH_RADIUS = 6371000;          // meters
export const EARTH_MU = 3.986004418e14;       // m^3/s^2 (Earth gravitational parameter)
export const SPEED_OF_LIGHT = 299792458;       // m/s
export const BOLTZMANN = 1.380649e-23;        // J/K
export const NOISE_TEMP = 290;                 // K
export const TRAIL_MAX = 20;                   // Maximum trail points for UEs
export const LEVY_CAP = 30;                    // Maximum pause rounds for Lévy walk
export const SIM_AREA_SIZE = 1000;             // km (simulation area size)

// ──────────────────────────────────────────────────────────────────────────────
// Orbital Constants
// ──────────────────────────────────────────────────────────────────────────────

export const ORBIT_DEFAULTS = {
  LEO: { altitude: 550_000, period: 5670 },      // ~94.5 min
  MEO: { altitude: 20_000_000, period: 43080 },  // ~12 hrs
  GEO: { altitude: 35_786_000, period: 86164 },  // 24 hrs (sidereal)
} as const;

export const WALKER_DELTA_PRESETS = {
  starlink: { numOrbitals: 12, numPlanes: 6, inclination: 53, altitude: 550 },
  oneweb:   { numOrbitals: 12, numPlanes: 12, inclination: 87.9, altitude: 1200 },
  kuiper:   { numOrbitals: 12, numPlanes: 7, inclination: 51.9, altitude: 630 },
  telesat:  { numOrbitals: 13, numPlanes: 6, inclination: 50.8, altitude: 1000 },
  irdium:   { numOrbitals: 11, numPlanes: 6, inclination: 86.4, altitude: 781 },
  globalstar: { numOrbitals: 8, numPlanes: 6, inclination: 52, altitude: 1410 },
} as const;

// ──────────────────────────────────────────────────────────────────────────────
// Channel Model Constants (3GPP TS 38.901)
// ──────────────────────────────────────────────────────────────────────────────

export const CHANNEL_DEFAULTS = {
  // NTN UMa LOS (Table 7.4.1-1)
  ntnLos: {
    pathLossExp: 2.0,      // Free space + atmospheric
    shadowingStd: 3,       // dB
    riceKFactor: 13,       // dB (strong LOS)
  },
  // NTN UMa NLOS
  ntnNlos: {
    pathLossExp: 3.5,
    shadowingStd: 8,
    riceKFactor: 0,
  },
  // Terrestrial UMa LOS (for comparison)
  terrestrialLos: {
    pathLossExp: 2.3,
    shadowingStd: 4,
    riceKFactor: 9,
  },
  // Terrestrial UMa NLOS
  terrestrialNlos: {
    pathLossExp: 3.67,
    shadowingStd: 7.8,
    riceKFactor: 0,
  }
} as const;

export const FREQUENCY_BANDS = {
  'n256': { downlink: 2.62, uplink: 2.5, band: 'S-band' },      // NTN
  'n510': { downlink: 1.98, uplink: 1.89, band: 'L-band' },     // NTN
  'n255': { downlink: 1.65, uplink: 1.63, band: 'L-band' },     // NTN
  'n78':  { downlink: 3.7, uplink: 3.4, band: 'C-band' },       // 5G NR FR1
  'n258': { downlink: 26, uplink: 24, band: 'Ka-band' },        // 5G NR FR2
  'n260': { downlink: 39, uplink: 37, band: 'Ka-band' },        // 5G NR FR2
  'n261': { downlink: 28, uplink: 27, band: 'Ka-band' },        // 5G NR FR2
} as const;

// ──────────────────────────────────────────────────────────────────────────────
// Visualization Constants
// ──────────────────────────────────────────────────────────────────────────────

export const SAT_COLORS = [
  '#0099cc', '#e04444', '#c89000', '#008855',
  '#7b4fcf', '#e07030', '#00a0a0', '#a000a0',
  '#a0a000', '#0066cc', '#cc0066', '#66cc00'
];

export const UE_PALETTE = [
  '#2aa89e', '#6aade4', '#e09030', '#a050d0',
  '#e06060', '#40b060', '#d05090', '#50b0c0',
  '#b09020', '#8050b0', '#20c080', '#c08020',
  '#8020c0', '#60c040', '#c04060', '#4060c0',
  '#a0c020', '#c020a0', '#20a0c0', '#60a020'
];

export const BS_COLORS = [
  '#0099cc', '#e04444', '#c89000', '#008855',
  '#7b4fcf', '#e07030'
];

export const SNR_THRESHOLDS = {
  GOOD: 15,    // dB
  MEDIUM: 5,   // dB
  POOR: 0      // dB
};

export function snrColor(snr: number): string {
  if (snr >= SNR_THRESHOLDS.GOOD) return '#008855';
  if (snr >= SNR_THRESHOLDS.MEDIUM) return '#c89000';
  return '#cc2233';
}

export function elevationColor(elev: number): string {
  if (elev >= 60) return '#008855';
  if (elev >= 30) return '#c89000';
  if (elev >= 10) return '#e07030';
  return '#cc2233';
}

export function stateColor(state: string): string {
  switch (state) {
    case 'ACTIVE': return '#008855';
    case 'AUTONOMOUS': return '#e07030';
    case 'FEEDER_OUTAGE': return '#cc2233';
    case 'ECLIPSE': return '#7b4fcf';
    case 'SYNCING': return '#0099cc';
    default: return '#888888';
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// xApp Configuration Defaults (matching oran-ntn)
// ──────────────────────────────────────────────────────────────────────────────

export const XAPP_DEFAULTS = {
  'ho-predict': {
    priority: 10,
    decisionInterval: 100,
    eventTriggers: ['periodic', 'measurement_change'] as string[],
    reportStyle: 'kpm_ue',
  },
  'beam-hop': {
    priority: 20,
    decisionInterval: 200,
    eventTriggers: ['periodic', 'beam_load_change'] as string[],
    reportStyle: 'kpm_cell',
  },
  'slice-manager': {
    priority: 30,
    decisionInterval: 500,
    eventTriggers: ['periodic', 'slice_sla_violation'] as string[],
    reportStyle: 'kpm_slice',
  },
  'doppler-comp': {
    priority: 15,
    decisionInterval: 200,
    eventTriggers: ['periodic', 'doppler_threshold'] as string[],
    reportStyle: 'kpm_ue',
  },
  'tn-ntn-steering': {
    priority: 25,
    decisionInterval: 500,
    eventTriggers: ['periodic', 'sinr_imbalance'] as string[],
    reportStyle: 'kpm_cell',
  },
  'energy-harvest': {
    priority: 12,
    decisionInterval: 1000,
    eventTriggers: ['periodic', 'battery_critical', 'eclipse_entry'] as string[],
    reportStyle: 'kpm_cell',
  },
  'interference-mgmt': {
    priority: 35,
    decisionInterval: 500,
    eventTriggers: ['periodic', 'interference_threshold'] as string[],
    reportStyle: 'kpm_cell',
  },
  'multi-conn': {
    priority: 22,
    decisionInterval: 500,
    eventTriggers: ['periodic', 'rlf_detected', 'candidate_available'] as string[],
    reportStyle: 'kpm_ue',
  },
  'predictive-alloc': {
    priority: 28,
    decisionInterval: 1000,
    eventTriggers: ['periodic', 'load_prediction'] as string[],
    reportStyle: 'kpm_slice',
  },
};

// ──────────────────────────────────────────────────────────────────────────────
// Action Types (matching E2RcActionType enum)
// ──────────────────────────────────────────────────────────────────────────────

export const ACTION_TYPES: Record<string, number> = {
  HANDOVER_TRIGGER: 0,
  HANDOVER_CANCEL: 1,
  BEAM_HOP_SCHEDULE: 2,
  BEAM_SWITCH: 3,
  SLICE_PRB_ALLOCATION: 4,
  DOPPLER_COMP_UPDATE: 5,
  TIMING_ADVANCE_UPDATE: 6,
  TX_POWER_CONTROL: 7,
  CCA_THRESHOLD_ADJUST: 8,
  BEAM_SHUTDOWN: 9,
  COMPUTE_THROTTLE: 10,
  INTERFERENCE_NULLING: 11,
  DC_SETUP: 12,
  DC_TEARDOWN: 13,
  BEARER_SPLIT: 14,
  PRB_RESERVATION: 15,
  ENERGY_PROFILE_UPDATE: 16,
};

export const ACTION_NAMES = Object.keys(ACTION_TYPES);

// ──────────────────────────────────────────────────────────────────────────────
// A1 Policy Types
// ──────────────────────────────────────────────────────────────────────────────

export const A1_POLICY_TYPES: Record<string, number> = {
  UE_THROUGHPUT: 0,
  UE_LATENCY: 1,
  UE_RELIABILITY: 2,
  CELL_LOAD: 3,
  SLICE_THROUGHPUT: 4,
  SLICE_LATENCY: 5,
  ENERGY_EFFICIENCY: 6,
  HANDOVER_RATE: 7,
  BEAM_UTILIZATION: 8,
  FL_CONVERGENCE: 9,
  FL_PRIVACY: 10,
};

// ──────────────────────────────────────────────────────────────────────────────
// Scheduler Types
// ──────────────────────────────────────────────────────────────────────────────

export const SCHEDULER_TYPES = [
  'Random', 'RoundRobin', 'MaxMin', 'MaxMax', 'DCC', 'Grover',
  'Spatial', 'Beamforming', 'VirtualMIMO',
  'NtnElevation', 'NtnDoppler', 'NtnFeederAware'
] as const;

// ──────────────────────────────────────────────────────────────────────────────
// FL Algorithm Types
// ──────────────────────────────────────────────────────────────────────────────

export const FL_ALGORITHMS = ['FedAVG', 'FedSGD', 'FedProx'] as const;

// ──────────────────────────────────────────────────────────────────────────────
// Channel Models
// ──────────────────────────────────────────────────────────────────────────────

export const CHANNEL_MODELS = ['AWGN', 'Rayleigh', 'Rician', 'NTN-LOS', 'NTN-NLOS'] as const;

// ──────────────────────────────────────────────────────────────────────────────
// Orbit Types
// ──────────────────────────────────────────────────────────────────────────────

export const ORBIT_TYPES = ['LEO', 'MEO', 'GEO'] as const;

// ──────────────────────────────────────────────────────────────────────────────
// Simulation Presets
// ──────────────────────────────────────────────────────────────────────────────

export const SIM_PRESETS = {
  // Quick demo: small constellation, few rounds
  demo: {
    ...DEFAULT_CONFIG,
    numOrbitals: 6,
    numPlanes: 3,
    numUEs: 10,
    totalRounds: 20,
    selectedPerRound: 4,
    roundDurationMs: 200,
  },
  // Standard: Starlink-like
  standard: {
    ...DEFAULT_CONFIG,
    numOrbitals: 12,
    numPlanes: 6,
    numUEs: 20,
    totalRounds: 50,
    selectedPerRound: 8,
    roundDurationMs: 300,
  },
  // Large: Full constellation simulation
  large: {
    ...DEFAULT_CONFIG,
    numOrbitals: 22,
    numPlanes: 6,
    numUEs: 50,
    totalRounds: 100,
    selectedPerRound: 16,
    roundDurationMs: 500,
  },
  // GEO: 3 GEO satellites
  geo: {
    ...DEFAULT_CONFIG,
    numOrbitals: 1,
    numPlanes: 3,
    orbitType: 'GEO',
    altitude: 35786,
    inclination: 0,
    enableISL: false,
    numUEs: 15,
    totalRounds: 30,
    selectedPerRound: 5,
    roundDurationMs: 500,
  },
  // FL-focused: more UEs, more rounds
  fl_focused: {
    ...DEFAULT_CONFIG,
    numOrbitals: 8,
    numPlanes: 4,
    numUEs: 40,
    totalRounds: 100,
    selectedPerRound: 12,
    localEpochs: 10,
    learningRate: 0.03,
    roundDurationMs: 200,
  },
} as const;

export function getPreset(name: keyof typeof SIM_PRESETS): SimConfig {
  return { ...SIM_PRESETS[name] };
}

export function mergeConfig(base: SimConfig, override: Partial<SimConfig>): SimConfig {
  return { ...base, ...override };
}